import {
  AgxButton,
  AgxColumn,
  AgxDivider,
  AgxDropdown,
  AgxGlobalSearch,
  AgxRow,
  AgxTable,
  AgxTypography,
  CampaignSearcherResult,
  DropdownOption,
  Icon_CheckmarkOutline,
  SearchResultType,
  useToast,
} from '@urbanx/agx.ui-components2';
import {
  UpdateCampaignStatusRequestStage,
  useGetUpdateCampaignStatusDetails,
  useInvalidateUseGetUpdateCampaignStatusDetails,
  useUpdateCampaignStatus,
  useUpdateContractStatus,
} from '@urbanx/agx.support.client';
import LoadingState from 'components/loadingState/LoadingState';
import Panel from 'components/panel/Panel';
import { useEffect, useMemo, useState } from 'react';
import SupportBackButton from '../SupportBackButton';

const UpdateCampaignStatus = () => {
  const { pushToast } = useToast();

  const [selectedCampaignId, setSelectedCampaignId] = useState<string>();
  const [selectedStage, setSelectedStage] = useState<string>();
  const [selectedContractStages, setSelectedContractStages] = useState<Record<string, string>>({});
  const [updatingContractId, setUpdatingContractId] = useState<string>();

  const {
    data: campaignDetails,
    isPending: isLoadingCampaignDetails,
    isError: hasCampaignDetailsError,
  } = useGetUpdateCampaignStatusDetails(selectedCampaignId ?? '', !!selectedCampaignId);

  const invalidateUpdateCampaignStatusDetails = useInvalidateUseGetUpdateCampaignStatusDetails();

  const { updateCampaignStatus, updateCampaignStatusPending } = useUpdateCampaignStatus({
    onSuccess: async (returnData, inputData) => {
      if (inputData.campaignId) {
        await invalidateUpdateCampaignStatusDetails(inputData.campaignId);
      }

      setSelectedStage(returnData?.currentStage);
      pushToast({
        message: 'Campaign status updated',
        type: 'success',
      });
    },
    onError: () => {
      pushToast({
        message: 'Error updating campaign status',
        type: 'error',
      });
    },
  });

  const { updateContractStatus, updateContractStatusPending } = useUpdateContractStatus({
    onSuccess: async (returnData, inputData) => {
      if (inputData.campaignId) {
        await invalidateUpdateCampaignStatusDetails(inputData.campaignId);
      }

      if (returnData?.contractId && returnData.currentStage) {
        const { contractId, currentStage } = returnData;
        setSelectedContractStages((stages) => ({
          ...stages,
          [contractId]: currentStage,
        }));
      }

      setUpdatingContractId(undefined);
      pushToast({
        message: 'Contract status updated',
        type: 'success',
      });
    },
    onError: () => {
      setUpdatingContractId(undefined);
      pushToast({
        message: 'Error updating contract status',
        type: 'error',
      });
    },
  });

  useEffect(() => {
    setSelectedStage(campaignDetails?.currentStage);
  }, [campaignDetails]);

  const onItemSelected = (itemType: SearchResultType, item: CampaignSearcherResult) => {
    if (
      itemType !== SearchResultType.ActiveCampaign &&
      itemType !== SearchResultType.ArchivedCampaign
    ) {
      return;
    }

    setSelectedCampaignId(item.campaignId);
    setSelectedStage(undefined);
    setSelectedContractStages({});
  };

  const selectedCampaignDetails = useMemo(() => {
    if (!campaignDetails) return [];

    return [
      { label: 'Property', value: campaignDetails.propertyAddress },
      { label: 'Agency', value: campaignDetails.agencyName },
      { label: 'Current status', value: campaignDetails.currentStageLabel },
    ].filter((detail) => !!detail.value);
  }, [campaignDetails]);

  const stageOptions = useMemo<DropdownOption[]>(
    () =>
      (campaignDetails?.potentialStages ?? [])
        .filter((stageOption) => !!stageOption.stage && !!stageOption.label)
        .map((stageOption) => ({
          label: stageOption.label ?? '',
          value: stageOption.stage ?? '',
        })),
    [campaignDetails]
  );

  const currentStage = campaignDetails?.currentStage;
  const hasSelectedCampaign = !!selectedCampaignId;
  const hasChanged = !!selectedStage && selectedStage !== currentStage;
  const isUpdatingStatus = updateCampaignStatusPending || updateContractStatusPending;

  const saveCampaignStatus = async () => {
    if (!campaignDetails?.campaignId || !selectedStage) return;

    await updateCampaignStatus({
      campaignId: campaignDetails.campaignId,
      stage: selectedStage as UpdateCampaignStatusRequestStage,
    });
  };

  const saveContractStatus = async (contractId: string, currentContractStage: string) => {
    const stage = selectedContractStages[contractId];
    if (!campaignDetails?.campaignId || !stage || stage === currentContractStage) return;

    setUpdatingContractId(contractId);
    await updateContractStatus({
      campaignId: campaignDetails.campaignId,
      contractId,
      stage,
    });
  };

  return (
    <AgxColumn gap="spacing-xl" fill>
      <SupportBackButton />
      <Panel title="Update Campaign and Contract Status">
        <AgxColumn gap="spacing-xl" fill>
          <AgxGlobalSearch
            searchForCampaigns
            searchForArchivedCampaigns
            onItemSelected={onItemSelected}
          />

          {isLoadingCampaignDetails && hasSelectedCampaign && <LoadingState />}

          {hasCampaignDetailsError && (
            <AgxTypography id="update-campaign-status-error" type="body" size="small">
              Unable to load campaign details.
            </AgxTypography>
          )}

          {campaignDetails && (
            <AgxColumn gap="spacing-xl" fill>
              <AgxColumn gap="spacing-xl" fill>
                <AgxTypography id="update-campaign-status-heading" type="title" size="medium">
                  Campaign status
                </AgxTypography>

                <AgxRow gap="spacing-xl" fill wrap>
                  {selectedCampaignDetails.map((detail) => (
                    <AgxColumn key={detail.label} gap="spacing-xs">
                      <AgxTypography
                        id={`update-campaign-status-${detail.label
                          .toLowerCase()
                          .replaceAll(' ', '-')}-label`}
                        type="body_ui"
                        size="small"
                      >
                        {detail.label}
                      </AgxTypography>
                      <AgxTypography
                        id={`update-campaign-status-${detail.label
                          .toLowerCase()
                          .replaceAll(' ', '-')}-value`}
                        type="body"
                        size="medium"
                      >
                        {detail.value}
                      </AgxTypography>
                    </AgxColumn>
                  ))}
                </AgxRow>

                <AgxRow gap="spacing-md" alignItems="flex-end" fill wrap>
                  <AgxDropdown
                    id="update-campaign-status-stage"
                    label="Status"
                    options={stageOptions}
                    selected={selectedStage}
                    onChange={setSelectedStage}
                    placeholder="Select status"
                    disabled={isUpdatingStatus}
                    stretch
                    hideOptionalText
                  />

                  <AgxButton
                    id="update-campaign-status-save"
                    text={updateCampaignStatusPending ? 'Saving' : 'Save'}
                    variant="primary"
                    leftIcon={<Icon_CheckmarkOutline width={16} height={16} />}
                    disabled={!hasChanged || isUpdatingStatus}
                    onClick={() => void saveCampaignStatus()}
                  />
                </AgxRow>
              </AgxColumn>

              <AgxDivider variant="horizontal" />

              <AgxColumn gap="spacing-xl" fill>
                <AgxTypography id="update-contract-status-heading" type="title" size="medium">
                  Contract statuses
                </AgxTypography>

                {campaignDetails.contracts?.length ? (
                  <AgxTable
                    variant="minimal"
                    tableStyle="square"
                    responsive
                    columns={[
                      'Contract',
                      { name: 'Current status', shrink: true },
                      'New status',
                      { name: 'Action', hideHeader: true, shrink: true },
                    ]}
                    rows={campaignDetails.contracts.map((contract, index) => {
                      if (
                        !contract.contractId ||
                        !contract.contractName ||
                        !contract.currentStage ||
                        !contract.currentStageLabel
                      ) {
                        throw new Error('Contract status details are incomplete.');
                      }

                      const { contractId, contractName, currentStage, currentStageLabel } =
                        contract;
                      const selectedContractStage =
                        selectedContractStages[contractId] ?? currentStage;
                      const hasContractChanged = selectedContractStage !== currentStage;
                      const isSavingContract =
                        updateContractStatusPending && updatingContractId === contractId;

                      return {
                        dataTestId: `update-contract-status-${contractId}-row`,
                        noHover: true,
                        Contract: (
                          <AgxColumn gap="spacing-xs">
                            <AgxTypography
                              id={`update-contract-status-${contractId}-number`}
                              type="body_ui_strong"
                              size="small"
                            >
                              {`Contract ${index + 1}`}
                            </AgxTypography>
                            <AgxTypography
                              id={`update-contract-status-${contractId}-name`}
                              type="body"
                              size="small"
                            >
                              {contractName}
                            </AgxTypography>
                          </AgxColumn>
                        ),
                        'Current status': (
                          <AgxTypography
                            id={`update-contract-status-${contractId}-current`}
                            type="body"
                            size="medium"
                          >
                            {currentStageLabel}
                          </AgxTypography>
                        ),
                        'New status': (
                          <AgxDropdown
                            id={`update-contract-status-${contractId}-stage`}
                            label={`Status for ${contractName}`}
                            options={(contract.potentialStages ?? []).flatMap((option) =>
                              option.stage && option.label
                                ? [{ label: option.label, value: option.stage }]
                                : []
                            )}
                            selected={selectedContractStage}
                            onChange={(stage) =>
                              setSelectedContractStages((stages) => ({
                                ...stages,
                                [contractId]: stage,
                              }))
                            }
                            placeholder="Select status"
                            disabled={isUpdatingStatus}
                            minWidth="220px"
                            stretch
                            noHeader
                            hideOptionalText
                          />
                        ),
                        Action: (
                          <AgxButton
                            id={`update-contract-status-${contractId}-save`}
                            text={isSavingContract ? 'Saving' : 'Save'}
                            variant="primary"
                            size="small"
                            leftIcon={<Icon_CheckmarkOutline width={16} height={16} />}
                            disabled={!hasContractChanged || isUpdatingStatus}
                            onClick={() => void saveContractStatus(contractId, currentStage)}
                          />
                        ),
                      };
                    })}
                  />
                ) : (
                  <AgxTypography id="update-contract-status-empty" type="body" size="small">
                    No contracts have been created for this campaign.
                  </AgxTypography>
                )}
              </AgxColumn>
            </AgxColumn>
          )}
        </AgxColumn>
      </Panel>
    </AgxColumn>
  );
};

export default UpdateCampaignStatus;
